import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  IconButton,
  Autocomplete,
  Chip,
  CircularProgress,
  Alert,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Card,
  CardContent,
  Divider,
  CardMedia,
  Stack,
  DialogContentText,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Restaurant as RestaurantIcon,
  AutoAwesome as AutoAwesomeIcon,
  Flag as FlagIcon,
  ImageSearch as ImageSearchIcon,
  Language as LanguageIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  FilterList as FilterIcon,
  Person as UserIcon,
  CalendarToday as DateIcon,
} from "@mui/icons-material";
import api from "../api";
import countriesData from "../constants/countries.json";
import TagSelector from "./TagSelector";
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from "../utils/languages";
import { processImageFile, validateImageFile } from "../utils/imageUtils";


// Main SuggestedRecipes Component
export default function SuggestedRecipes() {
  const [loading, setLoading] = useState(true);
  const [suggestedRecipes, setSuggestedRecipes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedRecipe, setEditedRecipe] = useState(null);
  console.log("🤔 ~ :63 ~ SuggestedRecipes ~ editedRecipe:", editedRecipe)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [savingChanges, setSavingChanges] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [ingredientErrors, setIngredientErrors] = useState({});

  // Fetch suggested recipes from API 
  const fetchSuggestedRecipes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/suggested-recipes?status=${statusFilter}`);
      
      if (response.data && response.data.success) {
        setSuggestedRecipes(response.data.data);
      } else {
        console.error('Failed to fetch suggested recipes');
        showSnackbar('Failed to fetch suggested recipes', 'error');
      }
    } catch (error) {
      console.error('Error fetching suggested recipes:', error);
      showSnackbar('Error fetching suggested recipes', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Fetch suggested recipes on component mount and when status filter changes
  useEffect(() => {
    fetchSuggestedRecipes();
  }, [fetchSuggestedRecipes]);
  
  // Handle recipe selection (opens detail dialog)
  const handleRecipeSelect = (suggestion) => {
    console.log("🤔 ~ :99 ~ handleRecipeSelect ~ suggestion:", suggestion)
    setSelectedSuggestion(suggestion);
    
    // Initialize editedRecipe with suggestion data
    setEditedRecipe({
      title: suggestion.recipe.title || '',
      summary: suggestion.recipe.summary || '',
      servings: suggestion.recipe.servings || 1,
      unitType: suggestion.recipe.unitType || 'gram',
      unitValue: suggestion.recipe.unitValue || 100,
      countryOfOrigin: suggestion.recipe.countryOfOrigin || '',
      image: suggestion.recipe.image || '',
      imageUrl: suggestion.recipe.imageUrl || '',
      extendedIngredients: suggestion.recipe.ingredients || [],
      selectedTagIds: suggestion.recipe.tags?.map(tag => tag.id) || [],
      // Nutritional values are often calculated from ingredients, but if present directly, use them
      calories: suggestion.recipe.calories?.toString() || '',
      protein: suggestion.recipe.protein?.toString() || '',
      fat: suggestion.recipe.fat?.toString() || '',
      carbs: suggestion.recipe.carbs?.toString() || '',
    });
    
    setEditMode(true);
  };
  
  // Handle closing the edit dialog
  const handleCloseEditDialog = () => {
    setSelectedSuggestion(null);
    setEditMode(false);
    setEditedRecipe(null);
  };
  
  // Handle input changes for recipe fields
  const handleInputChange = (field, value) => {
    setEditedRecipe(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle updating ingredient amount
  const handleUpdateIngredientAmount = (index, amount) => {
    const newIngredients = [...editedRecipe.extendedIngredients];
    const ingredient = newIngredients[index];
    const numericAmount = parseFloat(amount) || 0;

    ingredient.amount = numericAmount;

    // Recalculate nutrition if basePer100g is available
    if (ingredient.foodId && numericAmount > 0 && ingredient.basePer100g) {
      const factor = numericAmount / 100;
      ingredient.calories = ingredient.basePer100g.calories * factor;
      ingredient.protein = ingredient.basePer100g.protein * factor;
      ingredient.fat = ingredient.basePer100g.fat * factor;
      ingredient.carbs = ingredient.basePer100g.carbs * factor;
      ingredient.original = `${numericAmount} ${ingredient.unit} ${ingredient.name}`;
    }

    setEditedRecipe(prev => ({ ...prev, extendedIngredients: newIngredients }));
  };

  // Handle image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImageUploading(true);
    try {
      validateImageFile(file);
      const processedFile = await processImageFile(file);
      
      const formData = new FormData();
      formData.append("image", processedFile);

      const response = await api.post("/upload-image/public-admin/upload-recipe-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        handleInputChange('imageUrl', response.data.url);
        showSnackbar("Image uploaded successfully", "success");
      } else {
        throw new Error(response.data?.error || 'Failed to upload image');
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      showSnackbar(err.message || "Failed to upload image", "error");
    } finally {
      setImageUploading(false);
    }
  };

  // Calculate total nutrition for the recipe
  const calculateTotalNutrition = () => {
    if (!editedRecipe || !editedRecipe.extendedIngredients) return { calories: 0, protein: 0, fat: 0, carbs: 0 };

    const totals = editedRecipe.extendedIngredients.reduce(
      (acc, ing) => ({
        calories: Number(acc.calories) + Number(ing.calories || 0),
        protein: Number(acc.protein) + Number(ing.protein || 0),
        fat: Number(acc.fat) + Number(ing.fat || 0),
        carbs: Number(acc.carbs) + Number(ing.carbs || 0),
      }),
      { calories: 0, protein: 0, fat: 0, carbs: 0 }
    );
    return totals;
  };

  // Handle status filter change
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };
  
  // Handle approving a recipe suggestion
  const handleApprove = async () => {
    setSavingChanges(true);
    try {
      const payload = {
        ...editedRecipe,
        servings: editedRecipe.servings ? parseInt(editedRecipe.servings) : 1,
        unitValue: editedRecipe.unitValue ? parseFloat(editedRecipe.unitValue) : 100,
        calories: editedRecipe.calories ? parseFloat(editedRecipe.calories) : null,
        protein: editedRecipe.protein ? parseFloat(editedRecipe.protein) : null,
        fat: editedRecipe.fat ? parseFloat(editedRecipe.fat) : null,
        carbs: editedRecipe.carbs ? parseFloat(editedRecipe.carbs) : null,
        tagIds: editedRecipe.selectedTagIds || [],
      };
      delete payload.selectedTagIds;

      const response = await api.put(`/suggested-recipes/approve/${selectedSuggestion.id}`, payload);
      
      if (response.data && response.data.success) {
        showSnackbar('Recipe suggestion approved successfully', 'success');
        handleCloseEditDialog();
        fetchSuggestedRecipes();
      } else {
        showSnackbar(response.data?.error || 'Failed to approve recipe suggestion', 'error');
      }
    } catch (error) {
      console.error('Error approving recipe suggestion:', error);
      showSnackbar(error.response?.data?.error || 'Error approving recipe suggestion', 'error');
    } finally {
      setSavingChanges(false);
    }
  };
  
  // Open reject dialog
  const handleOpenRejectDialog = () => {
    setRejectDialogOpen(true);
  };
  
  // Close reject dialog
  const handleCloseRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectionReason('');
  };
  
  // Handle rejecting a recipe suggestion
  const handleReject = async () => {
    setSavingChanges(true);
    try {
      const response = await api.put(`/suggested-recipes/reject/${selectedSuggestion.id}`, {
        rejectionReason: rejectionReason
      });
      
      if (response.data && response.data.success) {
        showSnackbar('Recipe suggestion rejected', 'success');
        handleCloseRejectDialog();
        handleCloseEditDialog();
        fetchSuggestedRecipes();
      } else {
        showSnackbar('Failed to reject recipe suggestion', 'error');
      }
    } catch (error) {
      console.error('Error rejecting recipe suggestion:', error);
      showSnackbar('Error rejecting recipe suggestion', 'error');
    } finally {
      setSavingChanges(false);
    }
  };
  
  // Show snackbar notification
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };
  
  // Handle closing snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : "?";
  };
  
  const totalNutrition = calculateTotalNutrition();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      
      {/* Filter Bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ mr: 2 }}>Filter by Status:</Typography>
        </Box>
        <FormControl sx={{ minWidth: 150 }}>
          <Select
            size="small"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            displayEmpty
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Paper>
      
      {/* Main Content */}
      <Box>
        <Typography variant="h5" gutterBottom>
          {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)} Recipe Suggestions
        </Typography>
        
        <Divider sx={{ mb: 3 }} />
        
        {suggestedRecipes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography variant="h6" color="text.secondary">
              No {statusFilter} recipe suggestions found
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3} sx={{ justifyContent: 'flex-start' }}>
            {suggestedRecipes.map((suggestion) => (
              <Grid item xs={12} sm={6} lg={4} key={suggestion.id} sx={{ display: 'flex' }}>
                <Card 
                  sx={{ height: '100%', width: '100%', minWidth: 0, flex: '1 1 auto', display: 'flex', flexDirection: 'column', transition: 'box-shadow 0.3s ease-in-out', '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.1)', cursor: 'pointer' } }} 
                  elevation={2}
                  onClick={() => handleRecipeSelect(suggestion)}
                >
                  <CardMedia
                    component="img"
                    sx={{ height: 180, objectFit: 'contain', bgcolor: 'background.default', p: 2 }}
                    image={suggestion.recipe.image || "/api/placeholder/400/320"}
                    alt={suggestion.recipe.title}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" gutterBottom noWrap>
                      {suggestion.recipe.title}
                    </Typography>
                    
                    {suggestion.recipe.summary && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {suggestion.recipe.summary.length > 100 
                          ? `${suggestion.recipe.summary.substring(0, 100)}...`
                          : suggestion.recipe.summary
                        }
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <UserIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {suggestion.user?.firstName || 'Unknown User'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DateIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(suggestion.createdAt)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Chip 
                        label={suggestion.status.toUpperCase()}
                        color={
                          suggestion.status === 'pending' ? 'warning' :
                          suggestion.status === 'approved' ? 'success' : 'error'
                        }
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
      
      {/* Edit Recipe Dialog */}
      {selectedSuggestion && editMode && editedRecipe && (
        <Dialog 
          open={editMode} 
          onClose={handleCloseEditDialog}
          fullWidth
          maxWidth="xl"
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RestaurantIcon sx={{ mr: 1 }} />
              Recipe Suggestion Details
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
            <Box sx={{ width: '100%' }}>
              {/* Recipe Preview - Full Width */}
              <Paper elevation={3} sx={{ 
                p: 3, 
                mb: 3, 
                backgroundColor: 'white', 
                borderRadius: 3, 
                width: '100%',
                maxWidth: '100%',
                minWidth: '100%',
                boxSizing: 'border-box'
              }}>
                <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                  📱 Recipe Preview
                </Typography>
                
                <Box sx={{ textAlign: 'center', mb: 3, width: '100%' }}>
                  {editedRecipe.image || editedRecipe.imageUrl ? (
                    <Box sx={{ 
                      width: 150, 
                      height: 150, 
                      mx: 'auto', 
                      borderRadius: 3, 
                      overflow: 'hidden', 
                      border: '3px solid #e2e8f0'
                    }}>
                      <img 
                        src={editedRecipe.image || editedRecipe.imageUrl} 
                        alt={editedRecipe.title || "Recipe preview"} 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover'
                        }} 
                      />
                    </Box>
                  ) : (
                    <Box sx={{ 
                      width: 150, 
                      height: 150, 
                      mx: 'auto', 
                      borderRadius: 3, 
                      backgroundColor: '#3b82f6', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      color: 'white', 
                      fontSize: '4rem', 
                      fontWeight: 'bold', 
                      border: '3px solid #e2e8f0'
                    }}>
                      {getInitial(editedRecipe.title)}
                    </Box>
                  )}
                </Box>

                <Typography variant="h5" sx={{ 
                  fontWeight: 'bold', 
                  textAlign: 'center',
                  color: '#1e293b',
                  mb: 1
                }}>
                  {editedRecipe.title || "Recipe Title"}
                </Typography>
                
                <Typography variant="body2" sx={{ 
                  textAlign: 'center',
                  color: '#64748b',
                  mb: 1
                }}>
                  {editedRecipe.servings} servings
                </Typography>

                {editedRecipe.summary && (
                  <Typography variant="body2" sx={{ 
                    textAlign: 'center',
                    color: '#475569',
                    mb: 2,
                    px: 2,
                    fontStyle: 'italic',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.5
                  }}>
                    "{editedRecipe.summary}"
                  </Typography>
                )}

                {(editedRecipe.calories || editedRecipe.protein || editedRecipe.carbs || editedRecipe.fat) && (
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: 1,
                    mt: 2,
                    p: 2,
                    backgroundColor: '#f1f5f9',
                    borderRadius: 2,
                    width: '100%'
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary.main">{editedRecipe.calories || "0"}</Typography>
                      <Typography variant="caption" color="text.secondary">Calories</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary.main">{editedRecipe.protein || "0"}g</Typography>
                      <Typography variant="caption" color="text.secondary">Protein</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary.main">{editedRecipe.carbs || "0"}g</Typography>
                      <Typography variant="caption" color="text.secondary">Carbs</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color="primary.main">{editedRecipe.fat || "0"}g</Typography>
                      <Typography variant="caption" color="text.secondary">Fat</Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Box>

            <Stack spacing={3}>
                  <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>📝 Basic Information</Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12}><TextField fullWidth required label="Recipe Title" value={editedRecipe.title} onChange={(e) => handleInputChange("title", e.target.value)} /></Grid>
                      <Grid item xs={12}><TextField fullWidth label="Summary" value={editedRecipe.summary} onChange={(e) => handleInputChange("summary", e.target.value)} multiline rows={3} /></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Servings" type="number" value={editedRecipe.servings} onChange={(e) => handleInputChange("servings", parseInt(e.target.value) || 1)} InputProps={{ inputProps: { min: 1 } }} /></Grid>
                      <Grid item xs={12} md={6}><FormControl fullWidth><InputLabel>Unit Type</InputLabel><Select value={editedRecipe.unitType} label="Unit Type" onChange={(e) => handleInputChange("unitType", e.target.value)}><MenuItem value="gram">Gram</MenuItem><MenuItem value="ml">Milliliter</MenuItem><MenuItem value="piece_portion">Piece/Portion</MenuItem></Select></FormControl></Grid>
                      <Grid item xs={12} md={6}><TextField fullWidth label="Unit Value" type="number" value={editedRecipe.unitValue} onChange={(e) => handleInputChange("unitValue", parseFloat(e.target.value) || 100)} InputProps={{ inputProps: { min: 1 } }} /></Grid>
                      <Grid item xs={12} md={6}><FormControl fullWidth required><InputLabel>Country of Origin</InputLabel><Select value={editedRecipe.countryOfOrigin} label="Country of Origin" onChange={(e) => handleInputChange("countryOfOrigin", e.target.value)} MenuProps={{ PaperProps: { style: { maxHeight: 250, overflow: 'auto' } } }} renderValue={(selected) => (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar src={`/images/country/${selected.toLowerCase()}.svg`} sx={{ width: 24, height: 24 }}><FlagIcon fontSize="small" /></Avatar>{countriesData[selected]}</Box>)}>{Object.entries(countriesData).map(([code, name]) => (<MenuItem key={code} value={code}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar src={`/images/country/${code.toLowerCase()}.svg`} sx={{ width: 24, height: 24 }}><FlagIcon fontSize="small" /></Avatar>{name}</Box></MenuItem>))}</Select></FormControl></Grid>
                    </Grid>
                  </Paper>

                  <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>📷 Recipe Image</Typography>
                    <Box sx={{ textAlign: 'center' }}>
                      {(editedRecipe.image || editedRecipe.imageUrl) && (
                        <Box sx={{ mb: 2 }}>
                          <img src={editedRecipe.image || editedRecipe.imageUrl} alt={editedRecipe.title || "Recipe"} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }} />
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Button variant="contained" component="label" startIcon={imageUploading ? <CircularProgress size={20} /> : <UploadIcon />} disabled={imageUploading || imageGenerating}>
                          {imageUploading ? "Uploading..." : "Upload Image"}
                          <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                        </Button>
                      </Box>
                    </Box>
                  </Paper>

                  <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>🏷️ Recipe Tags</Typography>
                    <TagSelector selectedTagIds={editedRecipe.selectedTagIds} onTagsChange={(ids) => handleInputChange('selectedTagIds', ids)} label="Select tags for this recipe" placeholder="Choose relevant tags to categorize this recipe..." />
                  </Paper>

                  <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>🥘 Ingredients ({editedRecipe.extendedIngredients?.length || 0})</Typography>
                    {editedRecipe.extendedIngredients && editedRecipe.extendedIngredients.length > 0 ? (
                      <Box>
                        {editedRecipe.extendedIngredients.map((ingredient, index) => (
                          <Paper key={ingredient.id || index} sx={{ p: 2, mb: 2, border: ingredientErrors[index] ? '2px solid' : 'none', borderColor: 'error.main' }} elevation={2}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Ingredient Name" value={ingredient.name} onChange={(e) => {
                                  const newIngredients = [...editedRecipe.extendedIngredients];
                                  newIngredients[index].name = e.target.value;
                                  setEditedRecipe(prev => ({ ...prev, extendedIngredients: newIngredients }));
                                }} />
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <TextField fullWidth label="Amount" type="number" value={ingredient.amount} onChange={(e) => handleUpdateIngredientAmount(index, e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">{ingredient.unit}</InputAdornment> }} />
                              </Grid>
                              <Grid item xs={6} md={3}>
                                <TextField fullWidth label="Unit" value={ingredient.unit} onChange={(e) => {
                                  const newIngredients = [...editedRecipe.extendedIngredients];
                                  newIngredients[index].unit = e.target.value;
                                  setEditedRecipe(prev => ({ ...prev, extendedIngredients: newIngredients }));
                                }} />
                              </Grid>
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  <Chip label={`${Number(ingredient?.calories || 0)?.toFixed(1)} cal`} size="small" />
                                  <Chip label={`${Number(ingredient?.protein || 0)?.toFixed(1)}g protein`} size="small" />
                                  <Chip label={`${Number(ingredient?.fat || 0)?.toFixed(1)}g fat`} size="small" />
                                  <Chip label={`${Number(ingredient?.carbs || 0)?.toFixed(1)}g carbs`} size="small" />
                                </Box>
                              </Grid>
                            </Grid>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
                        No ingredients added yet.
                      </Typography>
                    )}
                  </Paper>

                  <Paper elevation={3} sx={{ p: 3, backgroundColor: 'white', borderRadius: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>🥗 Total Nutrition (per recipe)</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}><Paper sx={{ p: 2, textAlign: "center", bgcolor: "primary.light", color: "primary.contrastText" }}><Typography variant="h4">{Number(totalNutrition?.calories || 0)?.toFixed(0)}</Typography><Typography>Calories</Typography></Paper></Grid>
                      <Grid item xs={6} md={3}><Paper sx={{ p: 2, textAlign: "center", bgcolor: "secondary.light", color: "secondary.contrastText" }}><Typography variant="h4">{Number(totalNutrition?.protein || 0)?.toFixed(1)}g</Typography><Typography>Protein</Typography></Paper></Grid>
                      <Grid item xs={6} md={3}><Paper sx={{ p: 2, textAlign: "center", bgcolor: "warning.light", color: "warning.contrastText" }}><Typography variant="h4">{Number(totalNutrition?.fat || 0)?.toFixed(1)}g</Typography><Typography>Fat</Typography></Paper></Grid>
                      <Grid item xs={6} md={3}><Paper sx={{ p: 2, textAlign: "center", bgcolor: "info.light", color: "info.contrastText" }}><Typography variant="h4">{Number(totalNutrition?.carbs || 0)?.toFixed(1)}g</Typography><Typography>Carbs</Typography></Paper></Grid>
                    </Grid>
                  </Paper>

                  {/* Submission Information */}
                  <Paper elevation={3} sx={{ p: 3, mt: 3, backgroundColor: 'white', borderRadius: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary.main" sx={{ fontWeight: 'bold' }}>
                        📋 Submission Information
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Submitted by:</strong> {selectedSuggestion.user?.firstName || 'Unknown User'} ({selectedSuggestion.user?.email || 'No email'})</Typography></Grid>
                        <Grid item xs={12} sm={6}><Typography variant="body2"><strong>Submitted on:</strong> {formatDate(selectedSuggestion.createdAt)}</Typography></Grid>
                        <Grid item xs={12}><Typography variant="body2"><strong>Status:</strong> {selectedSuggestion.status.toUpperCase()}</Typography>
                        {selectedSuggestion.status === 'rejected' && selectedSuggestion.rejectionReason && (
                            <Typography variant="body2" color="error" sx={{ mt: 1 }}><strong>Rejection Reason:</strong> {selectedSuggestion.rejectionReason}</Typography>
                        )}</Grid>
                    </Grid>
                  </Paper>

            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleCloseEditDialog} color="inherit">Cancel</Button>
            <Box>
              {selectedSuggestion.status === 'pending' && (
                <>
                  <Button color="error" onClick={handleOpenRejectDialog} startIcon={<RejectIcon />} sx={{ mr: 1 }} disabled={savingChanges}>Reject</Button>
                  <Button color="success" variant="contained" startIcon={<ApproveIcon />} onClick={handleApprove} disabled={savingChanges}>{savingChanges ? 'Saving...' : 'Approve'}</Button>
                </>
              )}
            </Box>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleCloseRejectDialog}>
        <DialogTitle>Reject Recipe Suggestion</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please provide a reason for rejecting this recipe suggestion. This information may be shared with the user who submitted it.
          </DialogContentText>
          <TextField autoFocus label="Rejection Reason" fullWidth multiline rows={3} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRejectDialog} color="inherit">Cancel</Button>
          <Button color="error" variant="contained" onClick={handleReject} disabled={savingChanges}>{savingChanges ? 'Rejecting...' : 'Confirm Rejection'}</Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
